import React, { useState, useEffect, useMemo } from 'react';
import { validateCostBreakdown, logValidationErrors, hasOldBookingFeeBug } from '@/utils/validateCostBreakdown';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Divider,
  Stack,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Add,
  Download,
  DirectionsCar,
  FlashOn,
  People,
  Assignment,
  BookOnline,
  FilterList,
  Schedule,
  DateRange,
  MonetizationOn,
  Phone,
  Email,
  Edit,
  Delete,
  Visibility,
  Share,
  CreditCard,
  Cancel,
  CheckCircle,
  Warning,
  PersonAdd,
  VerifiedUser as VerificationIcon,
  CameraAlt as CheckinIcon,
  Refresh,
  Payment,
  AccountBalance,
  ContentCopy,
  Link as LinkIcon,
  WhatsApp,
  Business,
} from '@mui/icons-material';
import { bookingServiceV2 } from '@/services/bookingService.v2' ;
import { format, differenceInDays } from 'date-fns';
import { AdminBookingDetailsModal } from '@/components/bookings/AdminBookingDetailsModal';
import { CancellationDialog } from '@/components/bookings/CancellationDialog';
import { RefundDialog } from '@/components/bookings/RefundDialog';
import { ReleaseDepositDialog } from '@/components/bookings/ReleaseDepositDialog';
import { MultiStepBookingDialog } from '@/components/bookings/MultiStepBookingDialog';
import CreateVehicleBookingDialog from '@/components/bookings/CreateVehicleBookingDialog';
import { adminApiService } from '@/services/adminApiService';
import { vehicleAPI } from '@/services/vehicleAPI';
import { paystackPaymentService } from '@/services/paystackPaymentService';
import driverService from '@/services/driverService';
import toast from 'react-hot-toast';
import { buildApiUrl } from '@/utils/apiHelpers';
import { BookingV2 } from '@/types';

export const CarRentalsPage: React.FC = () => {
  const [allBookings, setAllBookings] = useState<BookingV2[]>([]); // Store server-filtered car rental bookings
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const [adminDetailsOpen, setAdminDetailsOpen] = useState(false);
  const [modalInitialTab, setModalInitialTab] = useState(0);
  const [advancedFilterOpen, setAdvancedFilterOpen] = useState(false);
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [createBookingOpen, setCreateBookingOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [packageSelectionOpen, setPackageSelectionOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<'flex' | 'premium' | null>(null);
  const [assignDriverOpen, setAssignDriverOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<any | null>(null);
  const [availableDrivers, setAvailableDrivers] = useState<any[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [cancellationDialogOpen, setCancellationDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [releaseDepositDialogOpen, setReleaseDepositDialogOpen] = useState(false);
  const [filterValues, setFilterValues] = useState({
    status: '',
    type: '',
    startDateFrom: '',
    startDateTo: '',
    minAmount: '',
    maxAmount: ''
  });

  // Payment link state
  const [paymentLinkDialogOpen, setPaymentLinkDialogOpen] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState<{
    paymentUrl: string;
    customerName: string;
    customerEmail: string;
    amount: number;
    isExisting: boolean;
    instructions?: {
      share?: string;
      validity?: string;
      webhook?: string;
    };
  } | null>(null);

  // New booking form state - Multi-step flow
  const [bookingStep, setBookingStep] = useState(1); // 1 = Search, 2 = Results, 3 = Payment
  const [customerTab, setCustomerTab] = useState(0); // 0 = Existing, 1 = New
  const [existingCustomers, setExistingCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [availableVehicles, setAvailableVehicles] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [searchingVehicles, setSearchingVehicles] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentReference, setPaymentReference] = useState<string>('');
  const [availableVehicleBodyTypes, setAvailableVehicleBodyTypes] = useState<string[]>([]);
  const [paymentLinkDialog, setPaymentLinkDialog] = useState({ open: false, link: '', bookingId: '', bookingNumber: '', email: '' });
  const [calculatedCosts, setCalculatedCosts] = useState<any>(null); // Store calculated cost breakdown
  const [calculatingCosts, setCalculatingCosts] = useState(false);
  const [bookingFormData, setBookingFormData] = useState({
    // Existing customer
    customerId: '',
    // New customer
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerNIN: '',
    customerDriverLicense: '',
    customerLocation: 'Lagos, Nigeria',
    // Booking details
    startDate: format(new Date(), 'yyyy-MM-dd'),
    startTime: '10:00',
    endDate: format(new Date(), 'yyyy-MM-dd'),
    endTime: '10:00',
    pickupLocation: '',
    dropoffLocation: '',
    vehicleTypes: [] as string[], // Multi-select
    transmission: '', // Automatic/Manual
    fuelType: '', // Petrol/Diesel/Electric/Hybrid
    notes: ''
  });

  const fetchBookings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Map selectedStatus to API status value (must match statusFilters array order)
      const statusMap: Record<number, string | undefined> = {
        0: undefined,   // All Status
        1: 'confirmed', // Confirmed
        2: 'cancelled', // Cancelled
        3: 'pending',   // Escalated (shows pending bookings)
        4: undefined    // Admin-Assisted (type filter, not status - handled client-side)
      };

      const result = await bookingServiceV2.getCarRentals({
        page: page + 1, // Backend uses 1-based pagination, MUI uses 0-based
        limit: rowsPerPage,
        status: statusMap[selectedStatus],
        search: searchTerm || undefined
      });

      setAllBookings(result.bookings);
      if (result.pagination) {
        setTotalCount(result.pagination.total);
      }
      console.log("bookings", result.bookings);
    } catch (err) {
      setError('Failed to fetch car rental bookings. Please try again.');
      console.error('❌ CarRentalsPage fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [page, rowsPerPage, selectedStatus, searchTerm]);

  // Fetch vehicle body types on mount
  useEffect(() => {
    const fetchVehicleBodyTypes = async () => {
      try {
        const url = buildApiUrl('mobile/vehicle-body-types', 'v2');
        const response = await fetch(url);
        const data = await response.json();

        if (data.success && data.data?.bodyTypes) {
          // Extract just the names for the dropdown
          const bodyTypeNames = data.data.bodyTypes.map((type: any) => type.name);
          setAvailableVehicleBodyTypes(bodyTypeNames);
        }
      } catch (error) {
        console.error('❌ Error fetching vehicle body types:', error);
        // Fallback to hardcoded list if API fails
        setAvailableVehicleBodyTypes(['Sedan', 'SUV', 'Van', 'Truck', 'Coupe', 'Convertible']);
      }
    };

    fetchVehicleBodyTypes();
  }, []);

  const handleViewDetails = (bookingId: string) => {
    setSelectedBookingId(bookingId);
    setModalInitialTab(0); // Details tab
    setAdminDetailsOpen(true);
  };

  const handleCloseModal = () => {
    setAdminDetailsOpen(false);
    setSelectedBookingId(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, bookingId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedBookingId(bookingId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Action Handlers
  const handleContactCustomer = () => {
    const booking = allBookings.find(r => r.id === selectedBookingId);
    if (!booking) return;

    const email = booking.user_email || ''
    const phone = booking.user_phone || '';
    const name = `${booking.user_first_name} ${booking.user_last_name}`;

    const message = `Contact ${name}:\n\nEmail: ${email}\nPhone: ${phone}`;

    if (window.confirm(`${message}\n\nChoose OK to send email, Cancel to call`)) {
      // Send email
      window.location.href = `mailto:${email}?subject=Regarding Booking ${booking.bookingNumber}`;
    } else if (phone) {
      // Call phone
      window.location.href = `tel:${phone}`;
    }
    handleMenuClose();
  };

  const handleGeneratePaymentLink = async () => {
    const booking = allBookings.find(r => r.id === selectedBookingId);
    if (!booking) return;

    handleMenuClose();

    try {
      const bookingId = booking.id;

      const loadingToast = toast.loading('Generating payment link...');

      const url = buildApiUrl(`admin/bookings/${bookingId}/generate-payment-link`, 'v2');
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          callbackUrl: `${window.location.origin}/payment-success`
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Backend error response:', errorData);
        const errorMessage = errorData.error?.details || errorData.error?.message || errorData.message || 'Failed to generate payment link';
        toast.dismiss(loadingToast);
        throw new Error(errorMessage);
      }

      const result = await response.json();

      const { paymentUrl, customerEmail, customerName, amount, instructions, isExisting } = result.data;

      toast.dismiss(loadingToast);
      await navigator.clipboard.writeText(paymentUrl);

      setPaymentLinkData({
        paymentUrl,
        customerName,
        customerEmail,
        amount,
        isExisting,
        instructions
      });
      setPaymentLinkDialogOpen(true);

      const toastMessage = isExisting ? 'Existing payment link retrieved!' : 'New payment link created!';
      toast.success(toastMessage, { duration: 3000 });

      await fetchBookings();

    } catch (error: any) {
      console.error('❌ Error generating payment link:', error);

      let errorMessage = 'Failed to generate payment link';
      if (error.message.includes('ALREADY_PAID')) {
        errorMessage = 'This booking has already been paid';
      } else if (error.message.includes('BOOKING_CANCELLED')) {
        errorMessage = 'Cannot generate payment link for cancelled booking';
      } else if (error.message.includes('MISSING_EMAIL')) {
        errorMessage = 'Customer email is required to generate payment link';
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage, { duration: 5000 });
    }
  };

  const handleShareDetails = () => {
    const booking = allBookings.find(r => r.id === selectedBookingId);
    if (!booking) return;

    const customerName = `${booking.user_first_name} ${booking.user_last_name}`;
    const shareText = `PolarRide Booking Details

Booking ID: ${booking.bookingNumber}
Customer: ${customerName}
Vehicle: ${booking.vehicle_name || 'N/A'}
Dates: ${booking.startDate} - ${booking.endDate}
Amount: ₦${parseFloat(booking.totalAmount).toLocaleString()}
Status: ${booking.status}`;

    // Try to use Web Share API if available
    if (navigator.share) {
      navigator.share({
        title: `Booking ${booking.bookingNumber}`,
        text: shareText,
      }).catch(() => {
        // Fallback to clipboard
        navigator.clipboard.writeText(shareText);
        alert('Booking details copied to clipboard!');
      });
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(shareText);
      alert('Booking details copied to clipboard!');
    }
    handleMenuClose();
  };

  const handleProcessRefund = () => {
    const booking = allBookings.find(r => r.id === selectedBookingId);
    if (!booking) return;

    setSelectedBooking(booking);
    setRefundDialogOpen(true);
    handleMenuClose();
  };

  const handleCancelBooking = () => {
    const booking = allBookings.find(r => r.id === selectedBookingId);
    if (!booking) return;

    setSelectedBooking(booking);
    setCancellationDialogOpen(true);
    handleMenuClose();
  };

  const handleReleaseDeposit = () => {
    const booking = allBookings.find(r => r.id === selectedBookingId);
    if (!booking) return;

    setSelectedBooking(booking);
    setReleaseDepositDialogOpen(true);
    handleMenuClose();
  };

  const handleRefundSuccess = () => {
    alert('Refund processed successfully!');
    fetchBookings(); // Refresh list
  };

  const handleCancellationSuccess = () => {
    alert('Booking cancelled successfully!');
    fetchBookings(); // Refresh list
  };

  const handleReleaseDepositSuccess = () => {
    toast.success('Deposit released successfully!');
    fetchBookings(); // Refresh list
  };

  const handleAdvancedFilter = () => {
    setAdvancedFilterOpen(true);
  };

  const handleEditBooking = (bookingId: string) => {
    setSelectedBookingId(bookingId);
    setModalInitialTab(2); // Modify tab
    setAdminDetailsOpen(true);
  };

  const handleExportData = () => {
    const csvContent = [
      ['Booking ID', 'Customer', 'Host Name', 'Host Email', 'Vehicle', 'Type', 'Start Date', 'End Date', 'Status', 'Amount (₦)'].join(','),
      ...filteredRentals.map(rental => [
        rental.bookingNumber,
        `${rental.user_first_name} ${rental.user_last_name}`,
        rental.host_first_name && rental.host_last_name
          ? `${rental.host_first_name} ${rental.host_last_name}`
          : 'N/A',
        rental.host_email || 'N/A',
        rental.vehicle_name || 'N/A',
        rental.bookingType,
        rental.startDate,
        rental.endDate,
        rental.status,
        rental.totalAmount || 0
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `car-rentals-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleNewBooking = () => {
    setCreateBookingOpen(true);
  };

  const handlePackageSelect = (packageType: 'flex' | 'premium') => {
    setSelectedPackage(packageType);
    setPackageSelectionOpen(false);
    setNewBookingOpen(true);
    setBookingStep(1); // Start with search step
    setSelectedVehicle(null);
    setSearchResults([]);
    // Fetch customers when opening booking dialog
    fetchCustomers();
  };

  const fetchCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const customersResponse = await adminApiService.getUsers();
      if (customersResponse.success && customersResponse.data.users) {
        setExistingCustomers(customersResponse.data.users);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchCustomersAndVehicles = async () => {
    // Fetch customers
    setLoadingCustomers(true);
    try {
      const customersResponse = await adminApiService.getUsers();
      if (customersResponse.success && customersResponse.data.users) {
        setExistingCustomers(customersResponse.data.users);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoadingCustomers(false);
    }

    // Fetch vehicles - try mobile endpoint first for approved vehicles
    setLoadingVehicles(true);
    try {

      // Try mobile endpoint first (most reliable for approved vehicles)
      let vehiclesResponse = await vehicleAPI.getAvailableVehicles({});

      if (!vehiclesResponse?.vehicles || vehiclesResponse.vehicles.length === 0) {
        // Fallback to admin endpoint
        vehiclesResponse = await vehicleAPI.getVehicles({ status: 'approved', limit: 100 });
      }

      if (vehiclesResponse?.vehicles && vehiclesResponse.vehicles.length > 0) {
        setAvailableVehicles(vehiclesResponse.vehicles);
      } else {
        setAvailableVehicles([]);
      }
    } catch (error) {
      console.error('❌ Error fetching vehicles:', error);
      setAvailableVehicles([]);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handleBookingFormChange = (field: string, value: any) => {
    setBookingFormData(prev => ({ ...prev, [field]: value }));

    // Auto-update end date when start date changes
    if (field === 'startDate' && value > bookingFormData.endDate) {
      setBookingFormData(prev => ({ ...prev, endDate: value }));
    }
  };

  const handleSearchVehicles = async () => {
    setSearchingVehicles(true);
    try {
      // Use the general /api/vehicles endpoint to avoid CORS issues
      const response = await vehicleAPI.getVehicles({
        status: 'approved',
        limit: 100
      });


      let results = response?.vehicles || [];

      // Filter by vehicle types if selected (case-insensitive partial matching)
      if (bookingFormData.vehicleTypes.length > 0) {
        const selectedTypes = bookingFormData.vehicleTypes.map(t => t.toLowerCase());

        const beforeFilterCount = results.length;
        results = results.filter((vehicle: any) => {
          // Use actual database fields: body (physical style) and type/vehicle_type (rental class)
          const vehicleBody = (vehicle.body || '').toLowerCase();
          const vehicleType = (vehicle.type || vehicle.vehicle_type || vehicle.vehicleType || '').toLowerCase();
          const vehicleMake = (vehicle.make || '').toLowerCase();

          const matches = selectedTypes.some(type =>
            vehicleBody.includes(type) ||
            type.includes(vehicleBody) ||
            vehicleType.includes(type) ||
            type.includes(vehicleType) ||
            vehicleMake.includes(type) ||
            type.includes(vehicleMake)
          );

          return matches;
        });
      }

      // Filter by transmission if specified
      if (bookingFormData.transmission) {
        const transmissionFilter = bookingFormData.transmission.toLowerCase();
        results = results.filter((vehicle: any) => {
          const vehicleTransmission = (vehicle.transmission || '').toLowerCase();
          return vehicleTransmission.includes(transmissionFilter);
        });
      }

      // Filter by fuel type if specified
      if (bookingFormData.fuelType) {
        const fuelFilter = bookingFormData.fuelType.toLowerCase();
        const beforeFuelFilter = results.length;

        results = results.filter((vehicle: any) => {
          const vehicleFuel = (vehicle.fuelType || vehicle.fuel || vehicle.fuel_type || '').toLowerCase();

          // If vehicle has no fuel type specified, include it (don't filter it out)
          if (!vehicleFuel) {
            return true;
          }

          const matches = vehicleFuel.includes(fuelFilter);

          return matches;
        });
      }

      // Filter by location if specified (case-insensitive partial match)
      if (bookingFormData.pickupLocation) {
        const locationFilter = bookingFormData.pickupLocation.toLowerCase();
        const beforeLocationFilter = results.length;

        results = results.filter((vehicle: any) => {
          const vehicleLocation = (vehicle.location || vehicle.city || '').toLowerCase();

          // If vehicle has no location specified, include it
          if (!vehicleLocation) {
            return true;
          }

          const matches = vehicleLocation.includes(locationFilter) ||
                         locationFilter.includes(vehicleLocation);

          return matches;
        });
      }

      // Sort by proximity to pickup location (simulated - in production, use actual distance)
      // For now, prioritize vehicles with matching location
      if (bookingFormData.pickupLocation) {
        results.sort((a: any, b: any) => {
          const aHasLocation = (a.location || '').toLowerCase().includes(bookingFormData.pickupLocation.toLowerCase());
          const bHasLocation = (b.location || '').toLowerCase().includes(bookingFormData.pickupLocation.toLowerCase());
          if (aHasLocation && !bHasLocation) return -1;
          if (!aHasLocation && bHasLocation) return 1;
          return 0;
        });
      }


      if (results.length === 0) {
        alert(`No vehicles found matching your criteria. Try adjusting your search filters.`);
        // Stay on search page
      } else {
        setSearchResults(results);
        setBookingStep(2); // Move to results page only if we have results
      }
    } catch (error) {
      console.error('❌ Error searching vehicles:', error);
      alert('Failed to search vehicles. Please try again.');
    } finally {
      setSearchingVehicles(false);
    }
  };

  const handleSelectVehicle = async (vehicle: any) => {
    setSelectedVehicle(vehicle);
    setBookingStep(3); // Move to payment page

    // Calculate costs immediately when entering payment step
    await calculateBookingCosts(vehicle);
  };

  // Calculate booking costs - called when entering payment step
  const calculateBookingCosts = async (vehicle?: any) => {
    const vehicleToUse = vehicle || selectedVehicle;
    if (!vehicleToUse) return;

    setCalculatingCosts(true);
    try {
      const vehicleId = vehicleToUse?.id || vehicleToUse?._id;
      if (!vehicleId) {
        console.error('❌ No vehicle ID found');
        return;
      }

      // Get customer ID - required for elite tier and first-time user discounts
      let customerId = null;

      if (customerTab === 0) {
        // Existing customer
        customerId = selectedCustomer?.id;
        if (!customerId) {
          console.error('❌ No customer selected');
          toast.error('Please select an existing customer first');
          setCalculatingCosts(false);
          return;
        }
      } else {
        // New customer - no discounts will apply (first booking)
      }

      const costCalculationPayload: any = {
        vehicleId,
        startDate: new Date(bookingFormData.startDate).toISOString(),
        endDate: new Date(bookingFormData.endDate).toISOString(),
        bookingType: 'daily',
        serviceCategory: 'vehicle-rental',
        requestDelivery: false,
        deliveryDistance: 0
      };

      // Only include customerId if we have one (existing customer)
      if (customerId) {
        costCalculationPayload.customerId = customerId;
      }


      const costUrl = buildApiUrl('admin/bookings/calculate-costs', 'v2');
      const costResponse = await fetch(costUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(costCalculationPayload)
      });

      const costResult = await costResponse.json();

      if (!costResponse.ok) {
        const errorMessage = costResult.error?.message || costResult.error || costResult.message || 'Unknown error';
        const errorDetails = costResult.error?.details || '';
        console.error('❌ Backend cost calculation failed:', {
          status: costResponse.status,
          statusText: costResponse.statusText,
          error: errorMessage,
          details: errorDetails,
          fullResponse: costResult
        });

        setCalculatedCosts(null);
        toast.error(`Failed to calculate costs: ${errorMessage}`);
        return;
      }

      const costData = costResult.data;
      setCalculatedCosts(costData);
    } catch (error) {
      console.error('❌ Cost calculation error:', error);
      setCalculatedCosts(null);
      alert('Failed to calculate costs. Please try again or contact support.');
    } finally {
      setCalculatingCosts(false);
    }
  };

  const handleProcessPayment = async (paymentMethod: 'pay-now' | 'send-link' = 'pay-now') => {
    // Prevent double-clicks or multiple calls
    if (processingPayment) {
      return;
    }


    setProcessingPayment(true);
    try {
      // Get customer email - handle both existing and new customers
      let customerEmail = '';
      let customerName = '';

      if (customerTab === 0) {
        // Existing customer
        customerEmail = selectedCustomer?.email || '';
        customerName = selectedCustomer?.name || 'Customer';
      } else {
        // New customer
        customerEmail = bookingFormData.customerEmail || '';
        customerName = bookingFormData.customerName || 'New Customer';
      }

      // Validate email
      if (!customerEmail || !customerEmail.includes('@')) {
        alert('Valid customer email is required for payment processing. Please enter the customer\'s email address.');
        setProcessingPayment(false);
        return;
      }

      // Use pre-calculated costs from payment step
      if (!calculatedCosts) {
        alert('Cost calculation not available. Please go back and try again.');
        setProcessingPayment(false);
        return;
      }

      const costData = calculatedCosts;
      const totalAmount = costData.totalCost;
      const days = differenceInDays(
        new Date(bookingFormData.endDate),
        new Date(bookingFormData.startDate)
      ) || 1;


      const adminId = localStorage.getItem('userId') || 'admin';
      let actualUserId = '';
      let createdBookingId = '';

      // Step 1: Create or get user ID
      if (customerTab === 0) {
        // Existing customer
        actualUserId = selectedCustomer?.id || '';
        if (!actualUserId) {
          alert('Please select a customer first.');
          setProcessingPayment(false);
          return;
        }
      } else {
        // New customer - create user first
        try {
          const customerPhone = bookingFormData.customerPhone || '';
          const customerLocation = bookingFormData.customerLocation || '';

          const createUserResponse = await adminApiService.createUser({
            name: customerName,
            email: customerEmail,
            phone: customerPhone,
            userType: 'renter',
            location: customerLocation
          });

          if (!createUserResponse.success || !createUserResponse.data?.id) {
            throw new Error(createUserResponse.error || 'Failed to create customer');
          }

          actualUserId = createUserResponse.data.id;
        } catch (userError) {
          console.error('❌ Failed to create customer:', userError);
          alert('Failed to create customer. Please try again.');
          setProcessingPayment(false);
          return;
        }
      }

      // Step 2: Create booking with calculated cost breakdown
      try {
        const bookingData = {
          userId: actualUserId,
          vehicleId: selectedVehicle?.id || selectedVehicle?._id,
          startDate: new Date(bookingFormData.startDate).toISOString(),
          endDate: new Date(bookingFormData.endDate).toISOString(),
          startTime: bookingFormData.startTime || '10:00',
          endTime: bookingFormData.endTime || '10:00',
          pickupLocation: bookingFormData.pickupLocation || 'Not specified',
          dropoffLocation: bookingFormData.dropoffLocation || bookingFormData.pickupLocation || 'Not specified',

          // ⚠️ CALCULATED VALUES FROM COST CALCULATION ENDPOINT
          totalAmount: costData.totalCost,
          dailyRate: costData.rentalCost,
          bookingFee: costData.bookingFee,
          securityDeposit: costData.securityDeposit?.amount || 0,

          // Full cost breakdown for receipts and admin tracking
          costBreakdown: {
            rentalCost: costData.rentalCost,
            bookingFee: costData.bookingFee,
            bookingFeeRate: costData.bookingFeeRate,
            serviceFee: costData.serviceFee,
            serviceFeeRate: costData.serviceFeeRate,
            vat: costData.vat,
            vatRate: costData.vatPercentage,
            securityDeposit: costData.securityDeposit?.amount || 0,
            totalAmount: costData.totalCost,
            hostPayout: costData.hostPayout
          },

          serviceCategory: 'vehicle-rental', // Required for mobile app filtering
          serviceType: selectedPackage === 'flex' ? 'self-drive' : 'with-driver',
          bookingType: 'admin_created',
          status: 'pending', // Valid statuses: pending, confirmed, cancelled, completed
          paymentStatus: 'pending',
          customerName: customerName,
          customerEmail: customerEmail,
          customerPhone: bookingFormData.customerPhone || '',
          notes: bookingFormData.notes || `Admin-created ${selectedPackage === 'flex' ? 'Self-Drive' : 'With Driver'} booking`,
          adminId: adminId
        };


        const createBookingResponse = await bookingServiceV2.createAdminBooking(bookingData);

        if (!createBookingResponse || !createBookingResponse.id) {
          throw new Error('Failed to create booking - no booking ID returned');
        }

        createdBookingId = createBookingResponse.id;
      } catch (bookingError) {
        console.error('❌ Failed to create booking:', bookingError);
        alert('Failed to create booking. Please try again.');
        setProcessingPayment(false);
        return;
      }

      // Step 3: Handle payment based on selected method
      const paymentResponse = await paystackPaymentService.initializePayment({
        amount: totalAmount,
        email: customerEmail,
        channel: 'card',
        metadata: {
          bookingId: createdBookingId,
          userId: actualUserId,
          bookingType: 'admin_created',
          adminId: adminId
        }
      });

      setPaymentReference(paymentResponse.data.reference);

      // If sending link, show payment link dialog
      if (paymentMethod === 'send-link') {
        const paymentLink = paymentResponse.data.authorizationUrl || paymentResponse.data.authorization_url;


        // Show payment link dialog
        setPaymentLinkDialog({
          open: true,
          link: paymentLink,
          bookingId: createdBookingId,
          bookingNumber: createBookingResponse.bookingNumber,
          email: customerEmail
        });

        setNewBookingOpen(false);
        setProcessingPayment(false);
        fetchBookings(); // Refresh bookings list
        return;
      }

      // For pay-now, open Paystack popup
      const popup = paystackPaymentService.openPaymentPopup(paymentResponse.data.authorizationUrl);

      // Step 4: Poll for payment status and update booking
      const cleanup = await paystackPaymentService.pollPaymentStatus(
        paymentResponse.data.reference,
        async (data) => {
          // Payment successful - update booking status

          try {
            // Update booking payment status
            const updateResponse = await bookingServiceV2.updateBooking(createdBookingId, {
              paymentStatus: 'paid',
              paymentReference: data.reference,
              paymentMethod: 'card',
              status: 'confirmed',
              paidAt: data.paidAt || new Date().toISOString()
            });


            if (!updateResponse || updateResponse.error) {
              throw new Error(updateResponse?.error || 'Update response was empty or invalid');
            }

            popup?.close();

            // Force refresh the bookings list
            await fetchBookings();

            toast.success('Payment successful! Booking created and confirmed.', {
              duration: 5000,
              position: 'top-center'
            });

            setNewBookingOpen(false);
            setProcessingPayment(false);

            // Additional safety: Refresh again after a short delay
            setTimeout(() => {
              fetchBookings();
            }, 2000);
          } catch (updateError: any) {
            console.error('❌ Failed to update booking status:', updateError);
            console.error('❌ Error details:', {
              message: updateError.message,
              stack: updateError.stack,
              bookingId: createdBookingId
            });
            popup?.close();
            alert(`Payment successful but failed to update booking status.\n\nBooking ID: ${createdBookingId}\nError: ${updateError.message}\n\nPlease refresh the page to see the updated status.`);
            setProcessingPayment(false);
            await fetchBookings();
          }
        },
        (status) => {
          // Payment failed
          popup?.close();

          // Provide more helpful error messages
          let errorMessage = 'Payment failed. The booking has been created but payment was not completed.';
          if (status === 'abandoned') {
            errorMessage = 'Payment was not completed. The booking is saved with pending payment status.';
          } else if (status === 'timeout') {
            errorMessage = 'Payment verification timed out. Please check your payment status or contact support.';
          } else if (status === 'error') {
            errorMessage = 'Unable to verify payment status. If you were charged, please contact support.';
          }

          alert(errorMessage);
          setProcessingPayment(false);
          fetchBookings(); // Refresh to show pending booking
        },
        () => {
          // Payment pending
        }
      );

      // Store cleanup function
      (window as any).__paymentCleanup = cleanup;

    } catch (error) {
      console.error('❌ Payment processing error:', error);
      alert('Failed to process payment. Please try again.');
      setProcessingPayment(false);
    }
  };

  const loadAvailableDrivers = async () => {
    try {
      const response = await driverService.getDrivers({});

      if (response.drivers && response.drivers.length > 0) {
        setAvailableDrivers(response.drivers);
      } else {
        setAvailableDrivers([]);
        toast.error('No active drivers available');
      }
    } catch (error) {
      console.error('Failed to load drivers:', error);
      setAvailableDrivers([]);
      toast.error('Failed to load available drivers');
    }
  };

  const handleAssignDriverClick = async (rental: any) => {
    setSelectedBooking(rental);
    setAssignDriverOpen(true);
    await loadAvailableDrivers();
  };

  const handleAssignDriver = async () => {
    if (!selectedBooking || !selectedDriverId) {
      toast.error('Please select a driver');
      return;
    }

    // Use dbId (actual database ID) instead of id (which might be booking reference)
    const bookingId = selectedBooking.dbId || selectedBooking.id;

    try {
      await bookingServiceV2.assignDriver(
        bookingId,
        selectedDriverId,
        'Assigned via car rentals page'
      );

      toast.success('Driver assigned successfully!');
      setAssignDriverOpen(false);
      setSelectedDriverId('');
      setSelectedBooking(null);
      loadRentals(); // Refresh the rentals list
    } catch (error: any) {
      console.error('Failed to assign driver:', error);
      toast.error(error.message || 'Failed to assign driver. Please contact backend team.');
    }
  };

  // Helper function to check if booking is self-drive (updated for real data)
  const isSelfDriveBooking = (b: any): boolean => {
    return b.serviceCategory === 'vehicle-rental' || 
           b.serviceCategory === 'vehicle_rental' ||
           b.serviceCategory === 'self_drive' ||
           b.type === 'vehicle-rental' ||
           b.type === 'self-drive' || 
           b.type === 'self_drive' ||
           (b.vehicle?.type === 'cars' && b.serviceCategory !== 'driver-hire');
  };

  // Helper function to check if booking is with-driver (updated for real data)  
  const isWithDriverBooking = (b: any): boolean => {
    // Based on our research, true "with-driver" car rentals are rare
    // Let's be more flexible and include potential with-driver indicators
    const hasDriverIndicators = b.serviceType === 'with-driver' || 
                                b.serviceType === 'chauffeur-driven' ||
                                b.serviceTypeEnhanced === 'with_driver' || 
                                b.serviceTypeEnhanced === 'chauffeur_driven' ||
                                b.serviceTypeEnhanced === 'personal_driver' ||
                                b.type === 'with-driver' ||
                                (b.notes && (
                                  b.notes.toLowerCase().includes('with driver') ||
                                  b.notes.toLowerCase().includes('chauffeur') ||
                                  b.notes.toLowerCase().includes('driver included')
                                ));
    
    return hasDriverIndicators;
  };

  // Helper function to check if booking is request-to-book (eliminates duplication)
  const isRequestToBookBooking = (b: any): boolean => {
    return b.approvalType === 'request_to_book' || b.approvalType === 'admin_approval' ||
           b.approvalType === 'dual_approval' || b.approvalType === 'approval-required' ||
           b.type === 'request-to-book' || b.bookingType === 'approval-required';
  };

  const handleTabChange = (newValue: number) => {
    setSelectedTab(newValue);

    // Debug logging for tab filtering
    if (newValue === 2) { // Self-Drive tab
      const selfDriveBookings = allBookings.filter(isSelfDriveBooking);
    } else if (newValue === 3) { // With Driver tab
      
      const withDriverBookings = allBookings.filter(isWithDriverBooking);
    }
  };

  const handleStatusChange = (newValue: number) => {
    setSelectedStatus(newValue);
    setPage(0); // Reset to first page on status filter change
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilterValues(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    // The filteredRentals useMemo will automatically apply these filters
    setAdvancedFilterOpen(false);
  };

  const handleResetFilter = () => {
    setFilterValues({
      status: '',
      type: '',
      startDateFrom: '',
      startDateTo: '',
      minAmount: '',
      maxAmount: ''
    });
  };

  // Booking types data - Performance Optimized Approach:
  // - Server-side filtered car rentals (no more client-side filtering)
  // - Badge counts calculated from pre-filtered data for accuracy
  // - Platform analytics remain in dashboard, this page shows specific car rental metrics
  const bookingTypes = [
    { 
      label: 'All Car Rentals', 
      icon: <Assignment sx={{ color: '#6366f1' }} />, 
      count: allBookings.length, // Real-time filtered count for accuracy
      bgColor: 'rgba(99, 102, 241, 0.1)',
      borderColor: '#6366f1'
    },
    { 
      label: 'Instant', 
      icon: <FlashOn sx={{ color: '#f59e0b' }} />, 
      count: allBookings.filter((b: any) => b.bookingType === 'instant').length, // Real-time count
      bgColor: 'rgba(245, 158, 11, 0.1)',
      borderColor: '#f59e0b'
    },
    {
      label: 'Self-Drive',
      icon: <DirectionsCar sx={{ color: '#10b981' }} />,
      count: allBookings.filter(isSelfDriveBooking).length, // Real-time count
      bgColor: 'rgba(16, 185, 129, 0.1)',
      borderColor: '#10b981'
    },
    {
      label: 'With Driver',
      icon: <People sx={{ color: '#8b5cf6' }} />,
      count: allBookings.filter(isWithDriverBooking).length, // Real-time count for accuracy
      bgColor: 'rgba(139, 92, 246, 0.1)',
      borderColor: '#8b5cf6'
    },
    {
      label: 'Request-to-Book',
      icon: <BookOnline sx={{ color: '#ef4444' }} />,
      count: allBookings.filter(isRequestToBookBooking).length, // Real-time count
      bgColor: 'rgba(239, 68, 68, 0.1)',
      borderColor: '#ef4444'
    },
  ];

  // Status filters - Performance Optimized: Server-filtered car rental counts
  const statusFilters = [
    { 
      label: 'All Status', 
      count: allBookings.length, // Real-time filtered count
      color: '#6366f1',
      bgColor: 'rgba(99, 102, 241, 0.1)',
      // icon: <Assignment sx={{ fontSize: 16 }} />
    },
    { 
      label: 'Confirmed', 
      count: allBookings.filter(b => b.status === 'confirmed').length, // Real-time count
      color: '#10b981',
      bgColor: 'rgba(16, 185, 129, 0.1)',
      // icon: <CheckCircle sx={{ fontSize: 16 }} />
    },
    { 
      label: 'Cancelled', 
      count: allBookings.filter(b => b.status === 'cancelled').length, // Real-time count
      color: '#ef4444',
      bgColor: 'rgba(239, 68, 68, 0.1)',
      // icon: <Cancel sx={{ fontSize: 16 }} />
    },
    { 
      label: 'Escalated', 
      count: allBookings.filter(b => b.status === 'pending').length, // Real-time count
      color: '#f59e0b',
      bgColor: 'rgba(245, 158, 11, 0.1)',
      // icon: <Warning sx={{ fontSize: 16 }} />
    },
    { 
      label: 'Admin-Assisted', 
      count: allBookings.filter(b => b.type === 'admin-assisted').length, // Real-time count
      color: '#8b5cf6',
      bgColor: 'rgba(139, 92, 246, 0.1)',
      // icon: <People sx={{ fontSize: 16 }} />
    },
  ];

  // Server handles search and status filtering, only apply tab, status (for Admin-Assisted), and advanced filters client-side
  const filteredAllBookings = useMemo(() => {
    const result = allBookings.filter(rental => {
      // Status filter for Admin-Assisted (client-side since it's a type, not status)
      if (selectedStatus === 4) {
        // Admin-Assisted filter
        if (rental.type !== 'admin-assisted') return false;
      }

      // Tab filter (booking type) - Client-side
      const matchesTab = (() => {
        switch (selectedTab) {
          case 0: return true; // All Car Rentals
          case 1: return rental.bookingType === 'instant'; // Instant Bookings
          case 2: return isSelfDriveBooking(rental); // Self-Drive
          case 3: return isWithDriverBooking(rental); // With Driver
          case 4: return isRequestToBookBooking(rental); // Request-to-Book
          default: return true;
        }
      })();

      // Advanced filter - Client-side
      const matchesAdvancedFilter = (() => {
        // Status filter from advanced dialog
        if (filterValues.status && rental.status !== filterValues.status) return false;

        // Type filter from advanced dialog
        if (filterValues.type && rental.bookingType !== filterValues.type) return false;

        // Date range filter
        if (filterValues.startDateFrom) {
          const rentalStart = new Date(rental.startDate);
          const filterStart = new Date(filterValues.startDateFrom);
          if (rentalStart < filterStart) return false;
        }

        if (filterValues.startDateTo) {
          const rentalStart = new Date(rental.startDate);
          const filterEnd = new Date(filterValues.startDateTo);
          if (rentalStart > filterEnd) return false;
        }

        // Amount range filter
        if (filterValues.minAmount) {
          const minAmount = parseFloat(filterValues.minAmount);
          const rentalAmount = parseFloat(rental.totalAmount);
          if (rentalAmount < minAmount) return false;
        }

        if (filterValues.maxAmount) {
          const maxAmount = parseFloat(filterValues.maxAmount);
          const rentalAmount = parseFloat(rental.totalAmount);
          if (rentalAmount > maxAmount) return false;
        }

        return true;
      })();

      return matchesTab && matchesAdvancedFilter;
    });

    return result;
  }, [allBookings, selectedTab, selectedStatus, filterValues]);

  // Then paginate the filtered results
  // Server already paginated, no need to slice
  const filteredRentals = useMemo(() => {
    return filteredAllBookings;
  }, [filteredAllBookings]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'cancelled': return 'error';
      case 'pending': return 'warning';
      case 'active': return 'info';
      case 'completed': return 'primary';
      default: return 'default';
    }
  };

  const getDepositStatusColor = (status: string | null) => {
    switch (status) {
      case 'held': return 'info';
      case 'released': return 'success';
      case 'partial_charged': return 'warning';
      case 'charged': return 'error';
      case 'refunded': return 'success';
      case 'pending': return 'default';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'instant': return <FlashOn sx={{ fontSize: 16 }} />;
      case 'self-drive': return <DirectionsCar sx={{ fontSize: 16 }} />;
      case 'with-driver': return <People sx={{ fontSize: 16 }} />;
      case 'request-to-book': return <BookOnline sx={{ fontSize: 16 }} />;
      default: return <DirectionsCar sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Car Rental Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage car rentals (self-drive and with-driver vehicles)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh bookings list">
            <IconButton
              onClick={() => {
                fetchBookings();
                toast.success('Refreshing bookings...', { duration: 2000 });
              }}
              color="primary"
              data-testid="refresh-bookings-btn"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ textTransform: 'none' }}
            onClick={handleExportData}
            data-testid="export-rentals-btn"
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ textTransform: 'none' }}
            onClick={handleNewBooking}
            data-testid="new-booking-btn"
          >
            New Car Rental
          </Button>
        </Box>
      </Box>



      {/* Main Content Card */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Booking Type Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={selectedTab}
              onChange={(_, newValue) => handleTabChange(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {bookingTypes.map((type, index) => (
                <Tab
                  key={index}
                  label={
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1.5,
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: selectedTab === index ? type.bgColor : 'transparent',
                      border: selectedTab === index ? `1px solid ${type.borderColor}` : '1px solid transparent',
                      transition: 'all 0.2s ease',
                      minWidth: 140
                    }}>
                      {type.icon}
                      <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                          {type.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {type.count} booking{type.count !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      <Chip
                        label={type.count}
                        size="small"
                        sx={{
                          bgcolor: selectedTab === index ? type.borderColor : type.bgColor,
                          color: selectedTab === index ? 'white' : type.borderColor,
                          fontWeight: 'bold',
                          minWidth: 28,
                          height: 22,
                          fontSize: '0.75rem',
                          border: selectedTab !== index ? `1px solid ${type.borderColor}` : 'none'
                        }}
                      />
                    </Box>
                  }
                  sx={{
                    textTransform: 'none',
                    minHeight: 70,
                    alignItems: 'flex-start',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Search and Filters - Consolidated Row */}
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
            {/* Search Bar Row */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                placeholder="Search by customer name, booking ID, or vehicle..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0); // Reset to first page on search
                }}
                size="small"
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'background.paper',
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'background.paper',
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'text.primary'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                startIcon={<FilterList />}
                onClick={handleAdvancedFilter}
                sx={{
                  textTransform: 'none',
                  borderRadius: 3,
                  px: 3,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: 2
                  },
                  transition: 'all 0.2s ease'
                }}
                data-testid="advanced-filter-btn"
              >
                Advanced Filter
              </Button>
            </Box>

            {/* Status Filter Chips - Inline */}
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              {statusFilters.map((status, index) => (
                <Chip
                  key={index}
                  icon={status.icon}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        {status.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          backgroundColor: selectedStatus === index ? 'rgba(255,255,255,0.3)' : status.color,
                          color: 'white',
                          px: 0.75,
                          py: 0.25,
                          borderRadius: 1,
                          fontWeight: 'bold',
                          fontSize: '0.65rem'
                        }}
                      >
                        {status.count}
                      </Typography>
                    </Box>
                  }
                  clickable
                  size="small"
                  variant={selectedStatus === index ? 'filled' : 'outlined'}
                  onClick={() => handleStatusChange(index)}
                  sx={{
                    backgroundColor: selectedStatus === index ? status.color : 'transparent',
                    borderColor: status.color,
                    color: selectedStatus === index ? 'white' : status.color,
                    fontWeight: 600,
                    height: 32,
                    '&:hover': {
                      backgroundColor: selectedStatus === index ? status.color : `${status.color}15`,
                      borderColor: status.color,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 2px 8px ${status.color}30`
                    },
                    transition: 'all 0.2s ease'
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* Bookings Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Booking ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Host</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Vehicle</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date Range</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                      <CircularProgress />
                      <Typography>Loading bookings...</Typography>
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                      <Alert severity="error">{error}</Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRentals.map((rental) => (
                    <TableRow key={rental.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {rental.bookingNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ bgcolor: rental.isCorporateBooking ? 'primary.main' : 'grey.500', fontSize: '1rem', fontWeight: 'bold' }}>
                            {rental.isCorporateBooking && rental.companyName
                              ? rental.companyName.charAt(0)
                              : `${rental.user_first_name} ${rental.user_last_name}`.charAt(0)
                            }
                          </Avatar>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                              {rental.isCorporateBooking && (
                                <Tooltip title="Corporate Account" arrow>
                                  <Business sx={{ fontSize: 14, color: 'primary.main' }} />
                                </Tooltip>
                              )}
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {rental.isCorporateBooking && rental.companyName
                                  ? rental.companyName
                                  : `${rental.user_first_name} ${rental.user_last_name}`
                                }
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {rental.user_phone && rental.user_phone !== 'N/A' ? (
                                <>
                                  <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {rental.user_phone}
                                  </Typography>
                                </>
                              ) : (
                                <>
                                  <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {rental.user_email}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {rental.host_first_name && rental.host_last_name
                            ? `${rental.host_first_name} ${rental.host_last_name}`
                            : 'N/A'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DirectionsCar sx={{ color: 'primary.main', fontSize: 20 }} />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {rental.vehicle_name || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          // Map bookingEntityType to serviceCategory
                          const serviceCategory = rental.bookingEntityType ? `${rental.bookingEntityType}-rental` : undefined;
                          const display = bookingServiceV2.getServiceTypeDisplayFull(serviceCategory, rental.serviceType);
                          return (
                            <Chip
                              label={display.label}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DateRange sx={{ color: 'primary.main', fontSize: 18 }} />
                          <Box>
                            {(() => {
                              // Check if this is an airport transfer
                              const isAirportTransfer = rental.bookingEntityType === 'airport' ||
                                                        rental.serviceCategory === 'airport_transfer' ||
                                                        rental.serviceCategory === 'airport-transfer';

                              if (isAirportTransfer) {
                                // For airport transfers, only show pickup date and time
                                return (
                                  <>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.25 }}>
                                      {format(new Date(rental.startDate), 'MMM dd, yyyy')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Schedule sx={{ fontSize: 12, color: 'text.secondary' }} />
                                      <Typography variant="caption" color="text.secondary">
                                        {format(new Date(rental.startDate), 'h:mm a')}
                                      </Typography>
                                    </Box>
                                  </>
                                );
                              } else {
                                // For rentals, show date range and duration
                                return (
                                  <>
                                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.25 }}>
                                      {format(new Date(rental.startDate), 'MMM dd, yyyy')} - {format(new Date(rental.endDate), 'MMM dd, yyyy')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Schedule sx={{ fontSize: 12, color: 'text.secondary' }} />
                                      <Typography variant="caption" color="text.secondary">
                                        {(() => {
                                          const days = differenceInDays(new Date(rental.endDate), new Date(rental.startDate));
                                          return `${days} day${days !== 1 ? 's' : ''}`;
                                        })()}
                                      </Typography>
                                    </Box>
                                  </>
                                );
                              }
                            })()}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip
                            label={rental.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            size="small"
                            color={getStatusColor(rental.status) as any}
                            sx={{ textTransform: 'capitalize' }}
                          />
                          {rental.securityDepositStatus && (
                            <Chip
                              label={`Deposit: ${rental.securityDepositStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}`}
                              size="small"
                              color={getDepositStatusColor(rental.securityDepositStatus) as any}
                              sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MonetizationOn sx={{ color: 'success.main', fontSize: 18 }} />
                          <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.main' }}>
                            ₦{(parseFloat(rental.totalAmount) || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              sx={{ color: 'primary.main' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewDetails(rental.id);
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="More Actions">
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, rental.id)}>
                              <MoreVert />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleContactCustomer} sx={{ gap: 1.5 }}>
          <Phone sx={{ fontSize: 16 }} /> Contact Customer
        </MenuItem>
        <MenuItem onClick={handleGeneratePaymentLink} sx={{ gap: 1.5 }}>
          <Payment sx={{ fontSize: 16 }} /> Generate Payment Link
        </MenuItem>
        <MenuItem onClick={handleShareDetails} sx={{ gap: 1.5 }}>
          <Share sx={{ fontSize: 16 }} /> Share Details
        </MenuItem>
        <MenuItem onClick={handleProcessRefund} sx={{ gap: 1.5 }}>
          <CreditCard sx={{ fontSize: 16 }} /> Process Refund
        </MenuItem>
        <MenuItem onClick={handleReleaseDeposit} sx={{ gap: 1.5 }}>
          <AccountBalance sx={{ fontSize: 16 }} /> Release Deposit
        </MenuItem>
        <MenuItem onClick={handleCancelBooking} sx={{ gap: 1.5, color: 'error.main' }}>
          <Delete sx={{ fontSize: 16 }} /> Cancel Booking
        </MenuItem>
      </Menu>

      {/* Advanced Filter Dialog */}
      <Dialog open={advancedFilterOpen} onClose={() => setAdvancedFilterOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Advanced Filter Options</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Booking Status</InputLabel>
                <Select 
                  value={filterValues.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Booking Type</InputLabel>
                <Select 
                  value={filterValues.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="instant">Instant</MenuItem>
                  <MenuItem value="self-drive">Self Drive</MenuItem>
                  <MenuItem value="with-driver">With Driver</MenuItem>
                  <MenuItem value="request-to-book">Request to Book</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date From"
                value={filterValues.startDateFrom}
                onChange={(e) => handleFilterChange('startDateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date To"
                value={filterValues.startDateTo}
                onChange={(e) => handleFilterChange('startDateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Min Amount (₦)"
                value={filterValues.minAmount}
                onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                InputProps={{ startAdornment: '₦' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Amount (₦)"
                value={filterValues.maxAmount}
                onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                InputProps={{ startAdornment: '₦' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvancedFilterOpen(false)}>Cancel</Button>
          <Button variant="outlined" onClick={handleResetFilter}>Reset Filters</Button>
          <Button variant="contained" onClick={handleApplyFilter}>Apply Filters</Button>
        </DialogActions>
      </Dialog>

      {/* Package Selection Dialog */}
      <Dialog
        open={packageSelectionOpen}
        onClose={() => setPackageSelectionOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Rental Package</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
            Choose the rental package for your customer
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    borderColor: 'primary.main',
                  },
                  border: '2px solid',
                  borderColor: 'divider',
                }}
                onClick={() => handlePackageSelect('flex')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <DirectionsCar sx={{ fontSize: 40, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Flex Package
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Self-drive rental - Customer drives
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Perfect for customers who want to drive themselves. No driver assignment needed.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                    borderColor: 'success.main',
                  },
                  border: '2px solid',
                  borderColor: 'divider',
                }}
                onClick={() => handlePackageSelect('premium')}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <People sx={{ fontSize: 40, color: 'success.main' }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Premium Package
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        With professional driver - Chauffeur service
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Includes a professional driver. Perfect for customers who want a chauffeur-driven experience.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageSelectionOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* New Booking Dialog - Multi-step */}
      <MultiStepBookingDialog
        open={newBookingOpen}
        onClose={() => setNewBookingOpen(false)}
        packageType={selectedPackage}
        step={bookingStep}
        customerTab={customerTab}
        onCustomerTabChange={setCustomerTab}
        existingCustomers={existingCustomers}
        selectedCustomer={selectedCustomer}
        onCustomerSelect={(customer) => setSelectedCustomer(customer)}
        loadingCustomers={loadingCustomers}
        formData={bookingFormData}
        onFormChange={(field, value) => {
          if (field === 'step') {
            setBookingStep(value);
          } else {
            handleBookingFormChange(field, value);
          }
        }}
        availableVehicleTypes={availableVehicleBodyTypes}
        onSearch={handleSearchVehicles}
        searchResults={searchResults}
        selectedVehicle={selectedVehicle}
        onSelectVehicle={handleSelectVehicle}
        onProcessPayment={handleProcessPayment}
        searchingVehicles={searchingVehicles}
        processingPayment={processingPayment}
        calculatedCosts={calculatedCosts}
        calculatingCosts={calculatingCosts}
      />

      {/* Assign Driver Dialog */}
      <Dialog
        open={assignDriverOpen}
        onClose={() => setAssignDriverOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Driver to Booking</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 2 }}>
            Select a driver to assign to this car rental booking
          </Typography>
          {selectedBooking && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Booking Details:
              </Typography>
              <Typography variant="body2">
                <strong>ID:</strong> {selectedBooking.bookingNumber}
              </Typography>
              <Typography variant="body2">
                <strong>Customer:</strong> {`${selectedBooking.user_first_name} ${selectedBooking.user_last_name}`}
              </Typography>
              <Typography variant="body2">
                <strong>Vehicle:</strong> {selectedBooking.vehicle_name || 'N/A'}
              </Typography>
            </Box>
          )}
          <FormControl fullWidth>
            <InputLabel>Select Driver</InputLabel>
            <Select
              value={selectedDriverId}
              onChange={(e) => setSelectedDriverId(e.target.value)}
              label="Select Driver"
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {availableDrivers.map((driver) => (
                <MenuItem key={driver.id} value={driver.id}>
                  {driver.firstName} {driver.lastName} - {driver.licenseNumber || 'No license'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {availableDrivers.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              No drivers available. Please add drivers first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDriverOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignDriver}
            disabled={!selectedDriverId}
            startIcon={<PersonAdd />}
          >
            Assign Driver
          </Button>
        </DialogActions>
      </Dialog>

      {/* Admin Booking Details Modal - Unified modal for all booking actions */}
      <AdminBookingDetailsModal
        open={adminDetailsOpen}
        onClose={() => {
          setAdminDetailsOpen(false);
          setSelectedBookingId(null);
        }}
        bookingId={selectedBookingId}
        initialTab={modalInitialTab}
        onBookingUpdated={() => {
          fetchBookings(); // Refresh bookings data
        }}
      />

      {/* Payment Link Dialog */}
      <Dialog
        open={paymentLinkDialog.open}
        onClose={() => setPaymentLinkDialog({ open: false, link: '', bookingId: '', bookingNumber: '', email: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle />
            <Typography variant="h6">Booking Created Successfully!</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Booking created with ID: <strong>{paymentLinkDialog.bookingNumber}</strong>
          </Alert>

          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Payment Link for {paymentLinkDialog.email}:
          </Typography>

          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.300',
              mb: 2,
              wordBreak: 'break-all'
            }}
          >
            <Typography
              variant="body2"
              component="a"
              href={paymentLinkDialog.link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {paymentLinkDialog.link}
            </Typography>
          </Box>

          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              navigator.clipboard.writeText(paymentLinkDialog.link);
              toast.success('Payment link copied to clipboard!');
            }}
            startIcon={<Assignment />}
          >
            Copy Link to Clipboard
          </Button>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2">
              Share this link with the customer via WhatsApp, Email, or SMS. They can click it to complete payment.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPaymentLinkDialog({ open: false, link: '', bookingId: '', bookingNumber: '', email: '' })}
            variant="contained"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Link Dialog */}
      <Dialog
        open={paymentLinkDialogOpen}
        onClose={() => setPaymentLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {paymentLinkData?.isExisting ? (
              <>
                <LinkIcon color="primary" />
                <Typography variant="h6">Existing Payment Link Retrieved</Typography>
              </>
            ) : (
              <>
                <CheckCircle color="success" />
                <Typography variant="h6">Payment Link Generated</Typography>
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {paymentLinkData && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer Details
                </Typography>
                <Card variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Name:</Typography>
                      <Typography variant="body2" fontWeight={600}>{paymentLinkData.customerName}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Email:</Typography>
                      <Typography variant="body2">{paymentLinkData.customerEmail}</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Amount:</Typography>
                      <Typography variant="h6" color="primary.main" fontWeight={700}>
                        ₦{paymentLinkData.amount.toLocaleString()}
                      </Typography>
                    </Box>
                  </Stack>
                </Card>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Payment Link
                </Typography>
                <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-all',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        color: 'primary.main'
                      }}
                    >
                      {paymentLinkData.paymentUrl}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ContentCopy />}
                      onClick={async () => {
                        await navigator.clipboard.writeText(paymentLinkData.paymentUrl);
                        toast.success('Link copied to clipboard!');
                      }}
                      fullWidth
                    >
                      Copy Link
                    </Button>
                  </Stack>
                </Card>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Next Steps
                </Typography>
                <Stack spacing={1}>
                  <Chip
                    icon={<WhatsApp />}
                    label={paymentLinkData.instructions?.share || 'Share this link with the customer via WhatsApp, SMS, or Email'}
                    variant="outlined"
                    color="success"
                    sx={{ justifyContent: 'flex-start', height: 'auto', py: 1.5, px: 1 }}
                  />
                  <Chip
                    icon={<Schedule />}
                    label={paymentLinkData.instructions?.validity || 'Link is valid until payment is completed'}
                    variant="outlined"
                    color="info"
                    sx={{ justifyContent: 'flex-start', height: 'auto', py: 1.5, px: 1 }}
                  />
                  <Chip
                    icon={<CheckCircle />}
                    label={paymentLinkData.instructions?.webhook || 'Payment status will update automatically when customer pays'}
                    variant="outlined"
                    color="primary"
                    sx={{ justifyContent: 'flex-start', height: 'auto', py: 1.5, px: 1 }}
                  />
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Quick Share
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<WhatsApp />}
                    color="success"
                    onClick={() => {
                      const message = `Hi ${paymentLinkData.customerName}, here's your payment link for ₦${paymentLinkData.amount.toLocaleString()}: ${paymentLinkData.paymentUrl}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
                    }}
                  >
                    WhatsApp
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Email />}
                    onClick={() => {
                      const subject = 'PolarRide Payment Link';
                      const body = `Hi ${paymentLinkData.customerName},\n\nPlease complete your payment of ₦${paymentLinkData.amount.toLocaleString()} using this link:\n\n${paymentLinkData.paymentUrl}\n\nThank you!`;
                      window.open(`mailto:${paymentLinkData.customerEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
                    }}
                  >
                    Email
                  </Button>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPaymentLinkDialogOpen(false)} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Vehicle Booking Dialog */}
      <CreateVehicleBookingDialog
        open={createBookingOpen}
        onClose={() => setCreateBookingOpen(false)}
        onSuccess={() => {
          setCreateBookingOpen(false);
          fetchBookings(); // Refresh bookings list
        }}
        initialType="rental"
      />

      {/* Cancellation Dialog */}
      <CancellationDialog
        open={cancellationDialogOpen}
        onClose={() => setCancellationDialogOpen(false)}
        bookingId={selectedBooking?.id || ''}
        onSuccess={handleCancellationSuccess}
      />

      {/* Refund Dialog */}
      <RefundDialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
        booking={selectedBooking}
        onSuccess={handleRefundSuccess}
      />

      {/* Release Deposit Dialog */}
      <ReleaseDepositDialog
        open={releaseDepositDialogOpen}
        onClose={() => setReleaseDepositDialogOpen(false)}
        booking={selectedBooking}
        onSuccess={handleReleaseDepositSuccess}
      />
    </Box>
  );
};